import { useEffect, useState } from 'react';
import {
  dismissReminderPrompt,
  hasReminderIntent,
  isInstalledPWA,
  isPushSupported,
  subscribePushNotifications,
  wasReminderPromptDismissed,
} from '../lib/pushSubscription.js';

type Phase =
  | { kind: 'hidden' }
  | { kind: 'idle' }
  | { kind: 'enabling' }
  | { kind: 'enabled' }
  | { kind: 'failed'; reason: string };

/**
 * Contextual nudge that appears next to the time picker when the
 * user pins a time-of-day on an obligation. The first time a
 * user crosses the "this happens at a specific moment" threshold,
 * we offer to wire up reminders inline rather than expecting
 * them to discover the always-on toggle elsewhere.
 *
 * The component is self-managing — parents just drop it in. It
 * decides on mount whether to show itself at all (push supported,
 * PWA installed on iOS, OS-level permission not denied, no prior
 * intent recorded, prompt not previously dismissed). Any failure
 * of those checks renders nothing, so it's safe to render
 * unconditionally wherever a `dueTime` value exists.
 *
 * User actions:
 *
 *  - Turn on → runs the subscribe flow. On success the prompt
 *              briefly shows a confirmation then hides; the
 *              always-available toggle takes over from here.
 *  - Not now → records the dismissal in localStorage so the
 *              prompt stays hidden in future captures/edits. A
 *              subsequent explicit "turn off" via the toggle
 *              clears that flag — answering "no thanks" doesn't
 *              outlive a re-enable.
 */
export function ReminderPrompt() {
  const [phase, setPhase] = useState<Phase>({ kind: 'hidden' });

  useEffect(() => {
    if (!isPushSupported()) return;
    // iOS treats a non-installed PWA as ineligible for push; the
    // user with that constraint sees the home-screen-install hint
    // elsewhere, not this prompt.
    if (!isInstalledPWA()) return;
    if (typeof Notification === 'undefined') return;
    if (Notification.permission === 'denied') return;
    if (hasReminderIntent()) return;
    if (wasReminderPromptDismissed()) return;
    setPhase({ kind: 'idle' });
  }, []);

  if (phase.kind === 'hidden') return null;

  async function enable() {
    setPhase({ kind: 'enabling' });
    try {
      await subscribePushNotifications();
      setPhase({ kind: 'enabled' });
      // Linger on the confirmation just long enough for the user
      // to register it, then unmount. The "Reminders" block at
      // the bottom of the Obligations tab now shows "On for this
      // device", which is the persistent surface from here on.
      window.setTimeout(() => setPhase({ kind: 'hidden' }), 2500);
    } catch (err) {
      const reason = err instanceof Error ? err.message : 'unknown';
      setPhase({ kind: 'failed', reason });
    }
  }

  function dismiss() {
    dismissReminderPrompt();
    setPhase({ kind: 'hidden' });
  }

  return (
    <div className="reminder-prompt" role="group" aria-label="Enable reminders">
      <span className="reminder-prompt__text">{messageFor(phase)}</span>
      <div className="reminder-prompt__actions">
        {phase.kind !== 'enabled' && (
          <button
            type="button"
            className="btn btn--secondary btn--small"
            onClick={enable}
            disabled={phase.kind === 'enabling'}
          >
            {primaryLabel(phase)}
          </button>
        )}
        {phase.kind !== 'enabled' && (
          <button
            type="button"
            className="btn btn--ghost btn--small"
            onClick={dismiss}
          >
            Not now
          </button>
        )}
      </div>
    </div>
  );
}

function messageFor(phase: Phase): string {
  switch (phase.kind) {
    case 'idle':
    case 'enabling':
      return 'Want a push when this is due?';
    case 'enabled':
      return 'Reminders are on for this device.';
    case 'failed':
      return failureText(phase.reason);
    case 'hidden':
      return '';
  }
}

function primaryLabel(phase: Phase): string {
  if (phase.kind === 'enabling') return 'Turning on…';
  if (phase.kind === 'failed') return 'Try again';
  return 'Turn on';
}

function failureText(reason: string): string {
  switch (reason) {
    case 'permission_denied':
      return "Notification permission was declined. Reset it in your browser settings to try again.";
    case 'not_configured':
      return 'Reminder service is not configured yet. Try again later.';
    case 'ios_not_installed':
      return 'Add Almanac to your home screen first, then come back here.';
    case 'unsupported':
      return 'Not supported on this browser.';
    default:
      return `Something went wrong (${reason}).`;
  }
}
